// src/lib/resa-or-block-loader.js — PR #169a-bis (ADR-105)
//
// Helper unifié de chargement résa OU pricing_block depuis Supabase pour
// ResaV2Modal. Discriminateur explicite `kind` (caller décide) — pas de
// probing automatique : appel SELECT cherchant dans 2 tables = perf gaspil
// + ambigu si même UUID (improbable mais théoriquement possible).
//
// Retourne un objet unifié `{ kind, id, raw, shape }` :
//   - `raw` : row Supabase brute (utile pour FooterActions block + audit)
//   - `shape` : reservation-shape canonique (12 voyageur + dates + montants)
//
// Pour kind='reservation' : raw === shape (pas de mapping).
// Pour kind='pricing_block' : shape = mapBlockToReservationShape(raw).
//
// Error handling :
//   - kind invalide → console.warn + Sentry breadcrumb + throw (BPR169a-bis-4)
//   - row introuvable → throw message UI-friendly
//   - erreur Supabase → throw avec message original

import { supabase } from "../supabase"
import { mapBlockToReservationShape } from "./sousloc-helpers"

const VALID_KINDS = ["reservation", "pricing_block"]

/**
 * Charge un item unifié (résa OU pricing_block) depuis Supabase.
 *
 * @param {string} id — UUID de l'item
 * @param {'reservation'|'pricing_block'} kind — type discriminant explicite
 * @returns {Promise<{
 *   kind: 'reservation' | 'pricing_block',
 *   id: string,
 *   raw: object,
 *   shape: object,
 * }>}
 * @throws {Error} kind_invalid, row introuvable, ou erreur Supabase
 */
export async function getResaOrBlock(id, kind) {
  if (!VALID_KINDS.includes(kind)) {
    // BPR169a-bis-4 — pas de fallback strict, signal explicite côté logs.
    // Pas de fallback strict : on throw direct pour ne pas charger la mauvaise table.
    // eslint-disable-next-line no-console
    console.warn(
      `[getResaOrBlock] kind invalide reçu : "${kind}" — attendu "reservation" ou "pricing_block". id=${id}`,
    )
    try {
      // Sentry breadcrumb (best-effort — Sentry peut ne pas être init en dev).
      if (typeof window !== "undefined" && window.Sentry?.addBreadcrumb) {
        window.Sentry.addBreadcrumb({
          category: "resa-or-block-loader",
          level: "warning",
          message: `kind invalide: ${kind}`,
          data: { id, kind },
        })
      }
    } catch (_) {
      /* silent */
    }
    throw new Error(`kind_invalid: ${kind}`)
  }

  if (kind === "reservation") {
    const { data, error } = await supabase
      .from("reservations")
      .select("*")
      .eq("id", id)
      .maybeSingle()
    if (error) throw new Error(error.message)
    if (!data) throw new Error("Réservation introuvable")
    return {
      kind: "reservation",
      id: data.id,
      raw: data,
      // reservations = shape canonique : pas de mapping requis.
      shape: data,
    }
  }

  // kind === 'pricing_block'
  const { data, error } = await supabase
    .from("pricing_blocks")
    .select("*")
    .eq("id", id)
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!data) throw new Error("Période bloquée introuvable")
  return {
    kind: "pricing_block",
    id: data.id,
    raw: data,
    shape: mapBlockToReservationShape(data),
  }
}
